import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Button,
  Card,
  EmptyState,
  FormField,
  Input,
  LoadingState,
  PageHeader,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/components/ui';
import {
  ADMIN_USER_ROLES,
  type AdminUserQuery,
  type AdminUserRole,
} from '@/api/admin';
import { useAdminUsers } from '@/hooks/useAdminUsers';
import {
  ImpersonateDialog,
  type ImpersonateTarget,
} from '@/components/admin/ImpersonateDialog';
import { formatAdminDate } from './admin-design-labels';
import { ADMIN_SEARCH_DEBOUNCE_MS, parsePage } from './admin-list-params';

/** URL search params the page reads and writes; shareable as a link. */
const ADMIN_USER_PARAMS = {
  search: 'search',
  role: 'role',
  page: 'page',
} as const;

const ROLE_LABEL: Record<AdminUserRole, string> = {
  USER: 'User',
  ADMIN: 'Admin',
  CUSTOMER_ADMIN: 'Customer admin',
};

function parseRole(value: string | null): AdminUserRole | undefined {
  return ADMIN_USER_ROLES.find((role) => role === value);
}

/**
 * Admin: all accounts, newest first, with their design count. Search, role
 * and page live in the URL so the design pages can link here
 * (`?search=<email>`). "Log in as user" opens the impersonation dialog;
 * admin accounts cannot be impersonated.
 */
export function AdminUsersPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const urlSearch = searchParams.get(ADMIN_USER_PARAMS.search) ?? '';
  const role = parseRole(searchParams.get(ADMIN_USER_PARAMS.role));
  const page = parsePage(searchParams.get(ADMIN_USER_PARAMS.page));

  const [searchInput, setSearchInput] = useState(urlSearch);
  const [impersonateTarget, setImpersonateTarget] =
    useState<ImpersonateTarget | null>(null);

  // Follow URL changes from outside the input (nav link, back button)
  useEffect(() => setSearchInput(urlSearch), [urlSearch]);

  useEffect(() => {
    document.title = 'User Admin – DesignDeck';
    return () => {
      document.title = 'DesignDeck';
    };
  }, []);

  /** Writes one filter to the URL and restarts at the first page. */
  const setFilter = (
    name: typeof ADMIN_USER_PARAMS.search | typeof ADMIN_USER_PARAMS.role,
    value: string,
    replace = false
  ) => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value) next.set(name, value);
        else next.delete(name);
        next.delete(ADMIN_USER_PARAMS.page);
        return next;
      },
      { replace }
    );
  };

  // Debounced search; only a changed value touches the URL, so the page
  // survives mounting with a prefilled field.
  useEffect(() => {
    const timer = setTimeout(() => {
      const search = searchInput.trim();
      if (search !== urlSearch) {
        setFilter(ADMIN_USER_PARAMS.search, search, true);
      }
    }, ADMIN_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only typing starts the debounce
  }, [searchInput]);

  const setPage = (nextPage: number) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (nextPage > 1) next.set(ADMIN_USER_PARAMS.page, String(nextPage));
      else next.delete(ADMIN_USER_PARAMS.page);
      return next;
    });
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchParams(new URLSearchParams());
  };

  const query: AdminUserQuery = {
    search: urlSearch || undefined,
    role,
    page,
  };

  const { data: result, isLoading, isError, refetch } = useAdminUsers(query);

  const users = result?.data ?? [];
  const meta = result?.meta;

  const activeFilters = [
    urlSearch ? `search "${urlSearch}"` : null,
    role ? `role ${ROLE_LABEL[role]}` : null,
  ].filter((filter): filter is string => filter !== null);

  const isEmpty = !isLoading && !isError && users.length === 0;

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <PageHeader
        eyebrow="Admin"
        title="Users"
        description="All accounts, newest first. Log in as a user to work inside their account."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <FormField label="Search">
          <Input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Email, name or designer ID"
          />
        </FormField>
        <FormField label="Role">
          <Select
            value={role ?? ''}
            onChange={(e) => setFilter(ADMIN_USER_PARAMS.role, e.target.value)}
          >
            <option value="">All roles</option>
            {ADMIN_USER_ROLES.map((value) => (
              <option key={value} value={value}>
                {ROLE_LABEL[value]}
              </option>
            ))}
          </Select>
        </FormField>
      </div>

      {isLoading && <LoadingState label="Loading users…" />}

      {isError && (
        <Card className="flex flex-col items-center gap-4 text-center">
          <p className="text-sm text-danger">Failed to load users.</p>
          <Button variant="outline" onClick={() => void refetch()}>
            Retry
          </Button>
        </Card>
      )}

      {isEmpty &&
        (activeFilters.length > 0 ? (
          <EmptyState
            title="No users match these filters"
            description={`Active filters: ${activeFilters.join(', ')}.`}
            action={
              <Button variant="outline" onClick={clearFilters}>
                Clear filters
              </Button>
            }
          />
        ) : (
          <EmptyState
            title="No users yet"
            description="Accounts appear here once they are registered or imported."
          />
        ))}

      {!isError && users.length > 0 && (
        <Table>
          <TableHead>
            <tr>
              <TableHeaderCell>Email</TableHeaderCell>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Role</TableHeaderCell>
              <TableHeaderCell>Designer ID</TableHeaderCell>
              <TableHeaderCell>Designs</TableHeaderCell>
              <TableHeaderCell>Created</TableHeaderCell>
              <TableHeaderCell>
                <span className="sr-only">Actions</span>
              </TableHeaderCell>
            </tr>
          </TableHead>
          <TableBody>
            {users.map((user) => {
              const isAdmin = user.role === 'ADMIN';
              const hintId = `impersonate-hint-${user.id}`;
              return (
                <TableRow key={user.id}>
                  <TableCell className="font-semibold text-ink">
                    {user.email}
                  </TableCell>
                  <TableCell className="text-ink-2">
                    {user.name || '—'}
                  </TableCell>
                  <TableCell className="text-ink-2">
                    {ROLE_LABEL[user.role]}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-ink-2">
                    {user.shirteeDesignerId ?? '—'}
                  </TableCell>
                  <TableCell>
                    <Link
                      to={`/admin/designs?ownerUserId=${encodeURIComponent(user.id)}`}
                      aria-label={`${user.designCount} designs of ${user.email}`}
                      className="font-mono text-ink underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      {user.designCount}
                    </Link>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-ink-2">
                    {formatAdminDate(user.createdAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={isAdmin}
                      aria-describedby={isAdmin ? hintId : undefined}
                      onClick={() =>
                        setImpersonateTarget({ id: user.id, email: user.email })
                      }
                    >
                      Log in as user
                    </Button>
                    {isAdmin && (
                      <span id={hintId} className="block text-xs text-mute">
                        Not possible for admin accounts
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {meta && meta.totalPages > 1 && (
        <nav
          aria-label="Pagination"
          className="flex flex-wrap items-center justify-center gap-4"
        >
          <Button
            variant="outline"
            onClick={() => setPage(Math.max(1, meta.page - 1))}
            disabled={!meta.hasPreviousPage}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            Previous
          </Button>
          <span className="font-mono text-sm text-mute">
            Page {meta.page} of {meta.totalPages} · {meta.total} users
          </span>
          <Button
            variant="outline"
            onClick={() => setPage(meta.page + 1)}
            disabled={!meta.hasNextPage}
          >
            Next
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </nav>
      )}

      <ImpersonateDialog
        target={impersonateTarget}
        onClose={() => setImpersonateTarget(null)}
      />
    </div>
  );
}
